import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable, resource, computed, Injector } from "@angular/core";
import { firstValueFrom, filter, take } from "rxjs";
import { toObservable } from "@angular/core/rxjs-interop";
import type { IFirm, IDashboardModel, IApplication } from "@app/views-interfaces";
import { AppSettingsService } from "@shared/app-settings-service";
import { ResponseErrorHandlerService } from "@shared/response-error-handler-service";
import type { IFirmsResponse } from "./firms-response.interface";
import { AuthService } from "@shared/auth";
import { DashboardsService } from "@shared/dashboards-service";

interface AccessibleApplicationsResource {
    accessibleDashboards: IDashboardModel[] | undefined;
    applications: IApplication[] | undefined;
}

interface AccessibleApplication {
    Key: string;
    Value: IDashboardModel[];
    Expanded: boolean;
}

@Injectable({ providedIn: "root" })
export class FirmsService {
    private readonly http = inject(HttpClient);
    private readonly authService = inject(AuthService);
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly injector = inject(Injector);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);

    private accessibleApplicationsCached: AccessibleApplication[] | undefined;
    private dashboardsService?: DashboardsService;
    private getDashboardsService(): DashboardsService {
        return (this.dashboardsService ??= this.injector.get(DashboardsService));
    }

    private readonly firmResource = resource<IFirm | undefined, string | undefined>({
        params: () => this.authService.currentFirmId(),
        loader: ({ params: firmId }) => {
            if (!firmId) return Promise.resolve(undefined);
            return this.loadFirm(firmId);
        },
    });
    public readonly firm = computed(() => this.firmResource.value());
    private readonly firm$ = toObservable(this.firm);
    public readonly applications = computed(() => this.firmResource.value()?.Applications);
    public readonly id = computed(() => this.firmResource.value()?.Id);

    private readonly accessibleApplicationsResource = resource<AccessibleApplication[] | undefined, AccessibleApplicationsResource>({
        params: () => ({
            accessibleDashboards: this.getDashboardsService().accessibleDashboards(),
            applications: this.applications(),
        }),
        loader: async ({ params }) => {
            const { accessibleDashboards, applications } = params;
            if (!accessibleDashboards || !accessibleDashboards.length || !applications || !applications.length) return;

            if (!this.accessibleApplicationsCached) this.accessibleApplicationsCached = [];
            accessibleDashboards.forEach((dashboard) => {
                const application = applications.find((x) => x.Value === dashboard.Id);
                if (application) {
                    let accessibleApplication = this.accessibleApplicationsCached?.find((x) => x.Key === application.Key);
                    if (!accessibleApplication) {
                        accessibleApplication = { Key: application.Key, Value: [], Expanded: false };
                        this.accessibleApplicationsCached?.push(accessibleApplication);
                    }
                    if (!accessibleApplication.Value.some((it) => it.Id === dashboard.Id)) {
                        accessibleApplication.Value.push(dashboard);
                    }
                }
            });

            if (this.accessibleApplicationsCached.length > 1) {
                this.accessibleApplicationsCached.sort((a, b) => {
                    if (a.Key < b.Key) return -1;
                    else if (a.Key > b.Key) return 1;

                    return 0;
                });
            }
            this.accessibleApplicationsCached.forEach((x) => {
                if (x.Value.length > 1) {
                    x.Value.sort((a, b) => {
                        if (a.CreationDate < b.CreationDate) return -1;
                        else if (a.CreationDate > b.CreationDate) return 1;

                        return 0;
                    });
                }
            });

            return this.accessibleApplicationsCached;
        },
    });
    public readonly accessibleApplications = computed(() => this.accessibleApplicationsResource.value());
    private readonly accessibleApplications$ = toObservable(this.accessibleApplications);

    private async loadFirm(id: string): Promise<IFirm | undefined> {
        try {
            const result = await firstValueFrom(
                this.http.get<IFirmsResponse>(this.getUrl("firms/" + id), {
                    headers: new HttpHeaders().set("Content-Type", "application/json"),
                }),
            );
            if (result.Code == 200) return result.Firm;
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
    }

    public async getFirm() {
        await this.waitForFirm();
        return this.firm();
    }

    // Do not use it. Only use in getFirm() for fallback compatibility. It will be removed in the future.
    private async waitForFirm(): Promise<void> {
        await firstValueFrom(
            this.firm$.pipe(
                filter((firm) => firm !== undefined && firm !== null),
                take(1),
            ),
        );
    }

    public async getAccessibleApplications() {
        await this.waitForAccessibleApplications();
        return this.accessibleApplications();
    }

    // Do not use it. Only use in getAccessibleApplications() for fallback compatibility. It will be removed in the future.
    private async waitForAccessibleApplications(): Promise<void> {
        await firstValueFrom(
            this.accessibleApplications$.pipe(
                filter((accessibleApplications) => accessibleApplications !== undefined && accessibleApplications !== null),
                take(1),
            ),
        );
    }

    private getUrl(path = "") {
        return (
            (this.appSettingsService.getSetting("FirmsService_URL") ?? "") + (this.appSettingsService.getSetting("FirmsService_Version") ?? "") + path
        );
    }
}
